import { useCallback, useEffect, useState } from "react";

interface CourseProgress {
  completedLessonIds: readonly string[];
  lastLessonCommit: string | null;
}

type UpdateCourseProgress = (currentProgress: CourseProgress) => CourseProgress;

const courseProgressStorageKey = "makefile-fyi:course-progress";
const emptyCourseProgress: CourseProgress = {
  completedLessonIds: [],
  lastLessonCommit: null,
};

// Track lightweight completion and resume state on the learner's device.
//
export function useCourseProgress() {
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState<CourseProgress>(emptyCourseProgress);

  useEffect(() => {
    setProgress(readCourseProgress());
    setIsReady(true);
  }, []);

  const updateProgress = useCallback((update: UpdateCourseProgress) => {
    setProgress((currentProgress) => {
      const nextProgress = update(currentProgress);
      if (nextProgress === currentProgress) {
        return currentProgress;
      }

      writeCourseProgress(nextProgress);
      return nextProgress;
    });
  }, []);

  const recordLessonVisit = useCallback(
    (commit: string) => {
      updateProgress((currentProgress) => {
        if (currentProgress.lastLessonCommit === commit) {
          return currentProgress;
        }

        return { ...currentProgress, lastLessonCommit: commit };
      });
    },
    [updateProgress],
  );

  const setLessonCompleted = useCallback(
    (lessonId: string, isComplete: boolean) => {
      updateProgress((currentProgress) => {
        const completedLessonIds = new Set(currentProgress.completedLessonIds);

        if (isComplete) {
          completedLessonIds.add(lessonId);
        } else {
          completedLessonIds.delete(lessonId);
        }

        return {
          ...currentProgress,
          completedLessonIds: Array.from(completedLessonIds),
        };
      });
    },
    [updateProgress],
  );

  return {
    completedLessonIds: progress.completedLessonIds,
    isReady,
    lastLessonCommit: progress.lastLessonCommit,
    recordLessonVisit,
    setLessonCompleted,
  };
}

// Read valid progress fields while ignoring stale or malformed storage data.
//
function readCourseProgress(): CourseProgress {
  try {
    const storedProgress = window.localStorage.getItem(
      courseProgressStorageKey,
    );
    if (!storedProgress) {
      return emptyCourseProgress;
    }

    const parsedProgress = JSON.parse(storedProgress) as {
      completedLessonIds?: unknown;
      lastLessonCommit?: unknown;
    };

    return {
      completedLessonIds: Array.isArray(parsedProgress.completedLessonIds)
        ? parsedProgress.completedLessonIds.filter(
            (lessonId): lessonId is string => typeof lessonId === "string",
          )
        : [],
      lastLessonCommit:
        typeof parsedProgress.lastLessonCommit === "string"
          ? parsedProgress.lastLessonCommit
          : null,
    };
  } catch {
    return emptyCourseProgress;
  }
}

// Persist progress when storage is available without blocking course use.
//
function writeCourseProgress(progress: CourseProgress) {
  try {
    window.localStorage.setItem(
      courseProgressStorageKey,
      JSON.stringify(progress),
    );
  } catch {
    // Local storage may be unavailable in private or restricted contexts.
  }
}
